import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { DatasetFolderRef } from "./datasetFolder";
import type { DatasetStorageBackend } from "./datasetStorageBackend";
import type { MemoPreview } from "./datasetMemo";
import type { ArmLayout } from "./datasetStructure";
import type { MemberRef } from "./member";
import type { Embodiment, Tip } from "./embodiment";
import type { Label } from "./label";
import type { PageQuery, PageResp } from "./page";

export type DatasetStatus = "pending" | "uploading" | "available" | "archived" | "cancelled";
export type DatasetSchemaVersion = "v0_sync" | "v1_async";
// 백엔드가 채널 구조에서 파생하는 수집 방식 축이다. 사용자가 편집하지 않는다.
export type RecordingMode = "teleoperation" | "direct_teaching";
// 행동을 만든 주체다. 구조로 판정할 수 없어서 업로더가 import 할 때 선언한다(upload init/reset).
// 선언하지 않으면 complete 가 422 로 막는다. 나중에 고치는 것은 admin PATCH 만 할 수 있다.
export type DataOrigin = "policy_rollout" | "human_demonstration";
// 카탈로그 응답의 리뷰 배지다. embodiment 가 sentinel(미지정)이면 needs_review, concrete 면
// reviewed 다. backend 가 컬럼 없이 embodiment_id 에서 파생한다
// (service/dataset/review_state.py::DatasetCatalogReviewState 와 1:1).
// 표시용 축이라 어떤 동작도 막지 않는다. 노출·학습·archive/restore 는 이 값과 관계없다.
export type DatasetCatalogReviewState = "needs_review" | "reviewed";
export type DatasetLabel = Label;

/**
 * 파생 산출 한 축의 상태다. 반입이 끝난 뒤 별도 잡이 실행한 결과다. 그래서 catalog
 * status(available/archived)와 **직교**한다. 이 값이 무엇이든 데이터셋은 이미 공개되어 있다.
 *
 * `ready` 는 "실행했고 끝났다"는 뜻이다(결과가 0건이어도 ready). 아직 실행한 적이 없으면
 * `not_computed` 다. 2단계 commit 도입 이전에 올라온 데이터셋에서는 이 값이 정상 상태다.
 */
export type DatasetProcessingAxisState =
  | "not_computed"
  | "processing"
  | "ready"
  | "partial"
  | "stale"
  | "failed"
  | "not_configured"
  // 업로드가 이 축의 집계(컬렉션 밴드·큐레이션)를 하지 않기로 선언했다. 이 값은 `parquet`
  // 축에만 온다. 에피소드 진단은 그 플래그와 관계없이 실행된다. `not_configured`(축이
  // 아직 연결되지 않음)와 달리 데이터셋별 결정이다. 운영 CLI 로 되돌릴 수 있다. 결과가 이미
  // 있으면 backend 는 이 값 대신 그 결과를 반환한다.
  | "declined";

/**
 * 축별 파생 상태 요약이다. 목록·상세·patch·archive·restore 응답에 모두 들어 있다.
 *
 * `labeling`/`annotation` 은 예약 축이라 항상 `not_configured` 다. 계약이 활성화되면
 * typecheck 가 소비처를 찾도록 타입에는 둔다. 화면에는 표시하지 않는다
 * (`components/dataset/datasetProcessing.ts` 가 렌더 대상 축을 소유한다).
 */
export type DatasetProcessing = {
  // 컬렉션 밴드 집계다(CollectionDiagnostic 합성). `band_diagnostics_enabled=false` 면 `declined` 다.
  parquet: DatasetProcessingAxisState;
  labeling: DatasetProcessingAxisState;
  annotation: DatasetProcessingAxisState;
};

// raw Dataset은 backend에서 공유 canonical이다. owner_member_uuid / public / origin_uuid는
// DatasetResp에 없으므로 frontend 타입에도 두지 않는다. 생성자 여부는 uuid 가 아니라
// 서버가 계산한 `can_write` 로만 받는다.
export type Dataset = {
  uuid: string;
  organization_uuid: string;
  artifact_source_uuid: string;
  // 정본이 있는 저장 위치다. 위치에 따라 쓸 수 있는 기능이 다르다(`datasetStorageBackend.ts`).
  storage_backend: DatasetStorageBackend;
  // 저장 위치가 학습을 지원하는지다. 서버가 판정한다. Dataset 의 상태(status)는 보지 않는다.
  // 그래서 학습 버튼은 status 와 이 값을 함께 본다.
  training_supported: boolean;
  // 하드웨어 프로필 embed 다. 목록의 식별 컬럼(embodiment / end_effector)의 SoT 다.
  // embodiment_is_unspecified sentinel 도 이 객체로 온다(이름이 unknown).
  embodiment: Embodiment;
  // 팔 구성이다. 실측 채널 이름의 좌우 방향으로 판정하므로 사용자가 편집하지 않는다.
  // null 은 미판정이다(방향 근거 없음 / 한쪽만 있음).
  arm_layout: ArmLayout | null;
  // EE 와 직교하는 tip 축이다. 선택하지 않은 dataset 은 null 이다.
  tip: Tip | null;
  display_name: string;
  // 담당자(operator) Member 참조다(uuid + 표시명). 미지정이면 null 이다. 백엔드가
  // Member FK 를 resolve 해서 반환한다. 그래서 목록/상세 표시에 별도 조회가 필요 없다.
  // 담당자 지정/변경은 정보 카드의 수정에서 uuid(operator_member_uuid)로 PATCH 한다.
  operator_member: MemberRef | null;
  status: DatasetStatus;
  // 축별 파생 진행 요약이다. status 와 직교한다. available 이어도 파생은 아직 실행 중일 수 있다.
  processing: DatasetProcessing;
  // 하드웨어 확정 여부에서 파생한 리뷰 배지다. 리뷰 큐에 남는지와는 관계없다.
  // 큐에 남는지는 아래 `reviewed` 가 정한다.
  review_state: DatasetCatalogReviewState;
  // 이 Dataset 의 설정이 한 번이라도 확정되었는지 나타낸다. 한 방향으로만 바뀐다. 그래서 확정
  // 뒤에 변경 요청이 올라와도 false 로 돌아가지 않는다. 큐에 남는지는 review_queue 가 정한다
  // (이 축과 요청 존재의 합집합). 새 업로드는 false 로 시작한다. 이 값은 노출·학습에 영향이 없다.
  reviewed: boolean;
  schema_version: DatasetSchemaVersion;
  // configuration과 같은 정규 컬럼이다. 자동 null은 내부 출처에서는 미판정, 외부 출처에서는
  // 해당 없음이다.
  recording_mode: RecordingMode | null;
  // 수동 null은 출처와 관계없이 해당 없음이다. 자동 재판정에서 제외한다.
  recording_mode_modified_manually: boolean;
  // 행동 주체다. 업로드 선언이나 configuration 저장·확정으로 고친다.
  data_origin: DataOrigin | null;
  total_episodes: number;
  // @compat ZERG-937 until=be:be56ea52
  quality_candidate_pending_count?: number;
  // 채널 파일이 없어서 invalid 인 소속 에피소드 수다(`invalid_reason=missing_channel`). 전역 제외한
  // 에피소드와 결함을 수용한 에피소드도 센다. 업로드는 이 에피소드를 처음부터 제외로 둔다.
  missing_channel_episode_count: number;
  // 실측 카메라 스트림 수다(데이터 inspect 집계, 상세 Cam Streams 와 같은 출처). embodiment
  // 카탈로그 스펙이 아니라 실제 녹화 결과가 기준이다.
  camera_stream_count: number;
  // primary anchor source 의 schema 밖(extra) 파일 수다. 상세 `extra_file_count` 와 스코프가 같다.
  extra_file_count: number;
  // 에피소드 길이의 중앙값(대표 길이)이다. "에피소드가 보통 얼마나 긴가"에 답한다. null 은
  // 아직 측정하지 않았다는 뜻이다(summary 미생성 / 길이 backfill 전).
  median_episode_length_sec: number | null;
  // 에피소드 길이의 합이다. "이 데이터셋에 영상이 얼마나 들어 있나"에 답한다. 중앙값과 다른
  // 축이다. duration 필터(`duration_min`/`duration_max`)가 보는 축과 같다.
  total_duration_sec: number | null;
  // 데이터셋 전체 용량이다. null 은 미집계다. 0 은 빈 데이터셋이라는 실제 값이다.
  total_size_bytes: number | null;
  created_at: string;
  modified_at: string;
  labels: readonly DatasetLabel[];
  // 대표 섬네일 presigned GET URL 이다. 없으면(생성 전·섬네일 미생성·failed) null 이다.
  // 그러면 화면은 이니셜로 fallback 한다.
  thumbnail_url: string | null;
  // 위 URL 의 만료 시각이다(ISO8601 UTC). thumbnail_url이 null이면 이 값도 null이다.
  thumbnail_url_expires_at: string | null;
  // 대표 섬네일을 추출한 video EpisodeFile의 uuid다. 섬네일을 클릭하면 이 uuid로 영상
  // signed URL을 요청한다(GET /v1/datasets/{uuid}/episode-files/{이 uuid}/video-url).
  // null이면(대표 video 미상) 영상 재생을 표시하지 않는다.
  representative_episode_file_uuid: string | null;
  // 메모(댓글) 요약이다(목록 카드/표 인디케이터용). BE 가 목록 응답에 항상 포함한다
  // (메모가 없으면 count=0·latest=null). 소비처는 별도 fetch 없이 이 값만 본다.
  memo_count: number;
  latest_memo: MemoPreview | null;
  // 이 데이터셋이 속한 폴더다(사람이 정리하는 축). 미분류면 null 이고, null 이 기본값이다.
  // 2계층이라서 `parent` 중첩 하나로 경로가 완성된다. 목록 행은 "Picking / 2026-Q1" 을
  // 추가 요청 없이 표시한다. 소속 변경은 PATCH 가 아니라 일괄 이동(bulkMove)이 소유한다.
  folder: DatasetFolderRef | null;
  // 요청자 기준 쓰기 자격이다(서버가 계산). 생성자나 admin 이면 true 다. 이 값은 메타 수정(PATCH)·
  // 보관·복원 버튼을 켠다. 설정 저장·확정의 자격은 설정 응답의 permissions 가 따로 담는다.
  can_write: boolean;
};

// 목록 정렬 축이다. backend service/dataset/sort.py::DatasetSort 와 1:1 이다. 통제 어휘라서
// 나열 밖 값은 422 다. 길이 축은 두 개다: `episode_length`(중앙값), `total_duration`(합).
// summary 캐시를 보는 네 축에서 값이 없는 행은 DB 기본 NULL 순서를 따른다(내림차순이면 맨 위).
export type DatasetSort =
  | "created_at"
  | "display_name"
  | "episode_length"
  | "total_duration"
  | "episode_count"
  | "total_size";

export type DatasetSortOrder = "asc" | "desc";

// 목록 facet 계약이다. backend api/v1/label_filters.py::ListFilterParams 와 1:1 이다.
// 모든 facet 값은 comma-separated 다. 결합 방식은 filter_match 하나가 정한다:
//   - all(기본): 모두 만족. 축 간 AND, 다중값 facet 안의 값 간 AND.
//   - any: 하나라도 만족. 축 간 OR, 값 간 OR.
// 에피소드 결함·행동 출처의 값 간 결합은 filter_match와 무관하게 OR다.
// search·status·reviewed·duration 은 항상 AND 게이트다.
// 하드웨어 축은 label 이 아니라 Embodiment 카탈로그로 필터한다(labels_all/any 에 넣으면 400).
export type DatasetListQuery = PageQuery & {
  search?: string;
  // 담당자 필터다. Member uuid 정확 매칭 또는 "me"(현재 actor)를 받는다. 이름 contains 가 아니다.
  // 잘못된 uuid 는 422 dataset.operator_invalid 다.
  operator?: string;
  labels_all?: string;
  labels_any?: string;
  // 라벨 facet 이다(reserved, confirmed 부여만 매칭). 세 축 모두 `unlabeled` 토큰은 **그 key 의
  // 라벨이 없는** 데이터셋을 고른다(값과 함께 오면 OR). 이 토큰은 조회 어휘라서 카탈로그 값으로
  // 등록할 수 없다.
  environment?: string;
  // 정본 위치 게이트다. 값은 하나다. 검색·폴더처럼 `filter_match` 와 무관하게 항상 AND 다. 생략하면
  // 저장 위치로 거르지 않는다.
  storage_backend?: DatasetStorageBackend;
  source?: string;
  task?: string;
  // 에피소드 결함 축이다. 데이터셋이 아니라 **에피소드**에 붙은 quality 태그(review-options 의
  // `episode_quality_tags`)로 거른다. 그 태그가 붙은 에피소드가 하나라도 있는 데이터셋이 남는다.
  // 값 대신 `any`(결함 에피소드가 하나라도 있음) · `none`(하나도 없음) 토큰도 받는다.
  // 전역 제외한 에피소드의 결함으로는 데이터셋이 걸러지지 않는다. 제외 짝(_not)은 없다.
  // 어휘 밖 값은 경고 없는 0건이 아니라 422 episode.invalid_quality_tag 다.
  episode_quality?: string;
  episode_quality_candidate?: string;
  // 에피소드 결함 **갈래** code 다(`/v1/label-categories?key=quality`). 갈래를 고르면 그 갈래의
  // 값을 모두 고른 것과 같다. `episode_quality` 와 **한 축**이다. 그래서 값·갈래·토큰은
  // filter_match 와 관계없이 항상 OR 다. 없는 code 는 422 episode.invalid_quality_category 다.
  episode_quality_category?: string;
  // 행동 출처 값과 none을 OR로 묶는다. 축 사이는 filter_match를 따른다.
  action_source?: string;
  // embodiment 카탈로그 facet
  embodiment?: string;
  // 팔 구성 facet 이다. single_arm / bimanual / unknown(미판정 선택). 값 간 OR 다.
  // 이 축은 카탈로그 선언이 아니라 데이터셋 실측 채널로 판정한다. unknown 은 컬럼 값이 아니라
  // NULL 을 고르는 조회 어휘다. 정의 밖 토큰은 400 facet.invalid_arms 다.
  arms?: string;
  end_effector?: string;
  end_effector_tag?: string;
  // 카메라 facet 이다. 데이터셋 실측 채널 인벤토리가 기준이다(camera_count=실측 채널 수,
  // camera_channel=distinct 채널명). `camera_count=0` 은 채널이 없는 데이터셋이다.
  camera_count?: string;
  camera_channel?: string;
  // tip 축 facet. `unknown` 은 미상 sentinel 과 미선택(NULL)을 함께 고른다.
  tip?: string;
  tip_tag?: string;
  // 수집 방식 facet 이다. 값 간 OR 다. 토큰:
  //   - teleoperation / direct_teaching
  //   - unknown: 미판정
  //   - inference: 추론 롤아웃
  //   - not_applicable: 외부 출처 데이터셋의 빈 값. 규격이 없어서 판정 대상이 아니다.
  // 다섯 토큰은 서로소다. inference 는 컬럼이 아니라 `data_origin` 을 보는 조회 어휘다.
  // 구조로는 롤아웃과 teleop 을 구분할 수 없기 때문이다.
  recording_mode?: string;
  // 폴더 축이다. 폴더 uuid 콤마 나열 또는 `unfiled`(미분류)를 받는다. 값 간 OR 다.
  // **부모를 고르면 자손까지 포함한다**(트리 노드의 dataset_count 와 이 질의의 total 이 같아야 한다).
  // 다른 facet 과 달리 `filter_match` 의 영향을 받지 않는 고정 게이트다. 그래서 항상 AND 다.
  // 부모가 다르면 이름이 중복될 수 있어서 이름은 키로 쓸 수 없다. 값은 항상 uuid 다.
  folder?: string;
  // 총 길이(초) 범위다. summary total_duration_sec 가 기준이다(facet 아님). inclusive 다.
  duration_min?: string;
  duration_max?: string;
  status?: DatasetStatus;
  // 검토 확정 축이다. 한 방향으로만 바뀐다. 그래서 확정한 뒤 설정이 다시 바뀌어도 "false" 로
  // 돌아가지 않는다. 리뷰 큐 목록은 이 축이 아니라 review_queue 를 쓴다. 미지정이면 거르지 않는다.
  reviewed?: "true" | "false";
  // 리뷰 큐 축이다. "true" 는 리뷰 페이지의 목록이다. 아래 두 집합의 합집합이다:
  //   - 아직 확정되지 않은 Dataset(reviewed=false)
  //   - 올라온 변경 요청(candidate)이 있는 Dataset
  review_queue?: "true" | "false";
  // 필터 결합 방식이다. all(기본, 모두 만족) / any(하나라도 만족). any 면 라벨(all/any)·facet·
  // 수집 방식·팔 구성 축을 OR 로 묶는다(값 간도 OR). 빈 축은 결합에 참여하지 않는다.
  // 단일값 축(embodiment 등)은 all 이어도 값 간 OR 다.
  filter_match?: "any" | "all";
  // 정렬 축과 방향이다. 둘 다 미지정이면 서버 기본값(생성 최신순)이다. 표 헤더가 정렬을 해제하면
  // 파라미터를 빼서 기본 정렬로 돌아간다.
  sort?: DatasetSort;
  order?: DatasetSortOrder;
};

export async function listDatasets(
  query: DatasetListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<Dataset>> {
  const response = await apiClient.get<PageResp<Dataset>>(apiRoutes.dataset.collection, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

export async function getDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.get<Dataset>(apiRoutes.dataset.detail(datasetUuid));
  return response.data;
}

// 보관한다(생성자 또는 admin).
export async function archiveDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.post<Dataset>(apiRoutes.dataset.archive(datasetUuid));
  return response.data;
}

// 일괄 보관 payload 다. 항목마다 호출하지 않는다(docs/rules.md §7).
export type DatasetBulkArchiveReq = {
  dataset_uuids: string[];
};

// 처리하지 못한 대상이다. 이미 그 상태이거나 전환이 막힌 항목이다(예: 업로드 중).
export type DatasetBulkArchiveSkipped = {
  dataset_uuid: string;
  reason: string;
};

export type DatasetBulkArchiveResp = {
  applied: number;
  skipped: DatasetBulkArchiveSkipped[];
};

// 한 번에 처리할 수 있는 상한이다. backend 검증과 같은 값이다(초과하면 422).
export const datasetBulkArchiveLimit = 100;

// 선택한 Dataset 을 한 요청으로 보관한다. 서버는 아래 경우에 전체를 거절하고 아무것도 적용하지 않는다:
//   - 하나라도 스코프 밖이면 404
//   - 요청자가 생성자나 admin 이 아닌 Dataset 이 하나라도 있으면 403
export async function bulkArchiveDatasets(
  datasetUuids: readonly string[],
): Promise<DatasetBulkArchiveResp> {
  const response = await apiClient.post<DatasetBulkArchiveResp>(apiRoutes.dataset.bulkArchive, {
    dataset_uuids: [...datasetUuids],
  } satisfies DatasetBulkArchiveReq);
  return response.data;
}

// 복원한다(생성자 또는 admin).
export async function restoreDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.post<Dataset>(apiRoutes.dataset.restore(datasetUuid));
  return response.data;
}

// GET /v1/datasets/camera-channels 응답이다. org 스코프의 distinct 카메라 채널명이다(정렬됨).
export type CameraChannelsResp = {
  channels: string[];
};

// camera_channel 필터 옵션의 소스다. 조직 Dataset의 실측 카메라 채널명 목록을 조회한다.
export async function listDatasetCameraChannels(): Promise<string[]> {
  const response = await apiClient.get<CameraChannelsResp>(apiRoutes.dataset.cameraChannels);
  return response.data.channels;
}

// 생성자도 admin 도 아닌 사람이 데이터셋 owner 기능을 호출했다(403). owner 기능은 메타 수정·
// 보관·복원·설정 저장·확정·fps·수집 방식이다. 버튼은 서버가 계산한 쓰기 자격으로 켠다.
// 그래도 목록 선택이나 열어 둔 창의 행 스냅샷이 오래되면 이 에러가 난다.
export function isDatasetWriteForbidden(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "dataset.write_forbidden";
}
